import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, FileText, DollarSign, TrendingUp, Building2, MapPin, Flag, Send, Upload, BarChart3, Eye, UserCircle, CheckCircle, AlertTriangle } from "lucide-react";
import EntityLink, { Breadcrumbs } from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import ProfileDocuments from "../components/shared/ProfileDocuments";
import { Entity360Shell, MetricCard, TimelineCard, ReputationBadge, ReputationDetailCard } from "../components/shared/OSComponents";
import { fmtUSD } from "../utils/currency";
import Contact360Drawer from "../components/admin/Contact360Drawer";

import { useAuth } from "../context/AuthContext";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
import { stageLabel, stageBadgeColor } from "../lib/stageConstants";
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

export default function Borrower360Page({ userId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("deals");
  const [reputation, setReputation] = useState(null);
  const [showContact360, setShowContact360] = useState(false);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);

  useEffect(() => {
    axios.get(`${API}/entity/borrower/${userId}/360`, { headers }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    axios.get(`${API}/reputation/borrower/${userId}`, { headers }).then(r => setReputation(r.data)).catch(() => {});
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Borrower not found</div>;

  const { profile, stats, deals, documents, profile_doc_checklist: docChecklist, timeline } = data;
  const crumbs = [...breadcrumbs, { type: "borrower", id: userId, label: profile.full_name || "Borrower" }];
  const relatedEntities = [
    ...deals.map(d => ({ type: "deal", id: d.lead_id, title: `${LOAN_LABELS[d.loan_type] || d.loan_type} — ${d.state || ""}`, subtitle: d.property_address })),
    ...documents.slice(0, 3).map(d => ({ type: "document", id: d.doc_id, title: d.original_filename, subtitle: d.doc_type })),
  ];
  const quickActions = [
    { id: "view_deals", label: "View All Deals", icon: Building2, onClick: () => setTab("deals") },
    ...(isInternal ? [{ id: "contact360", label: "Open Contact 360", icon: UserCircle, onClick: () => setShowContact360(true) }] : []),
    ...(isInternal ? [{ id: "request_docs", label: "Request Documents", icon: Upload, onClick: () => setTab("documents") }] : []),
    ...(isInternal ? [{ id: "add_note", label: "Add Note", icon: Send, onClick: () => setTab("notes") }] : []),
    ...(isInternal ? [{ id: "generate_report", label: "Generate Report", icon: BarChart3, onClick: () => {} }] : []),
  ];
  const profileDocsComplete = docChecklist ? docChecklist.every(d => d.uploaded) : false;
  const tabs = [
    { key: "deals", label: `Deals (${deals.length})` },
    { key: "profile_docs", label: `Profile Docs ${profileDocsComplete ? "✓" : `(${docChecklist?.filter(d => !d.uploaded).length || 0} missing)`}` },
    { key: "reputation", label: "Reputation" },
    { key: "documents", label: `Deal Documents (${documents.length})` },
    ...(timeline.length ? [{ key: "timeline", label: "Timeline" }] : []),
    ...(isInternal ? [{ key: "comms", label: "Communications" }] : []),
    ...(isInternal ? [{ key: "notes", label: "Notes" }] : []),
  ];
  const kpis = [
    { label: "Total Deals", value: stats.total_deals, icon: Building2 },
    { label: "Funded Deals", value: stats.funded_deals, icon: TrendingUp, accent: true },
    { label: "Total Volume", value: fmtUSD(stats.total_volume), icon: DollarSign },
    { label: "Capital Raised", value: fmtUSD(stats.total_capital_raised), icon: DollarSign },
  ];
  const badges = [
    ...(profile.flagged ? [{ label: "Flagged", className: "bg-red-100 text-red-600 border border-red-200" }] : []),
    ...(profile.suspended ? [{ label: "Suspended", className: "bg-orange-100 text-orange-600 border border-orange-200" }] : []),
  ];

  return (
    <Entity360Shell type="borrower" title={profile.full_name} subtitle={[profile.email, profile.created_at && `Since ${new Date(profile.created_at).toLocaleDateString()}`].filter(Boolean).join(" | ")}
      badges={badges} kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab} onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity}
      quickActions={quickActions} reputation={reputation?.score} relatedEntities={relatedEntities}>
      {tab === "deals" && (
        <div className="space-y-3">
          {deals.map(d => (
            <div key={d.lead_id} className="rounded-xl border border-[#e8e0d0] bg-white p-4 flex items-center justify-between hover:shadow-sm transition-shadow cursor-pointer" onClick={() => onNavigate("deal", d.lead_id)} data-testid={`borrower-deal-${d.lead_id}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]"><Building2 className="h-4 w-4 text-emerald-600" /></div>
                <div>
                  <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#222]">{LOAN_LABELS[d.loan_type] || d.loan_type}</span><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${stageBadgeColor(d.stage)}`}>{stageLabel(d.stage)}</span></div>
                  <div className="flex gap-3 text-[10px] text-[#999] mt-0.5">{d.state && <span>{d.state}</span>}{d.property_address && <span className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{d.property_address}</span>}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {d.term_sheets > 0 && <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">{d.term_sheets} TS</span>}
                {d.interests > 0 && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{d.interests} interests</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "profile_docs" && docChecklist && (
        <div className="space-y-3" data-testid="profile-doc-checklist">
          <div className="text-xs text-[#666] mb-2">
            Required documents for borrower onboarding. These documents live on the borrower's profile and are accessible across all views.
          </div>
          {docChecklist.map((doc, i) => (
            <div key={doc.type} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${doc.uploaded ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}`} data-testid={`profile-doc-${doc.type}`}>
              {doc.uploaded ? (
                <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              )}
              <div className="flex-1">
                <p className="text-xs font-semibold text-[#333]">{doc.label}</p>
                <p className="text-[10px] text-[#999]">{doc.uploaded ? "Uploaded" : "Missing — required for onboarding"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "reputation" && <ReputationDetailCard data={reputation} entityType="Borrower" />}
      {tab === "documents" && <ProfileDocuments userId={userId} documents={documents} isAdmin={isInternal} onRefresh={() => { axios.get(`${API}/entity/borrower/${userId}/360`, { headers }).then(r => setData(r.data)).catch(console.error); }} />}
      {tab === "timeline" && <TimelineCard events={timeline} />}
      {tab === "comms" && <EntityComms entityType="borrower" entityId={userId} />}
      {tab === "notes" && <EntityNotes entityType="borrower" entityId={userId} />}

      {/* Contact 360 Drawer — opens from Quick Actions */}
      {showContact360 && isInternal && (
        <Contact360Drawer
          open={showContact360}
          onClose={() => setShowContact360(false)}
          userId={userId}
          userType="borrower"
          userName={profile.full_name}
          onViewDeal={(leadId) => onNavigate?.("deal", leadId)}
          onEntityNavigate={navigateToEntity}
        />
      )}
    </Entity360Shell>
  );
}
